import { type CSSProperties, useEffect, useState } from 'react';
import { getMonthGrid } from '../lib/calendarGrid';
import { getPrintGeometry, type PrintGeometry } from '../lib/printGeometry';
import { usePhotoPreviewUrls } from '../lib/usePhotoPreviewUrls';
import { loadLastProject } from '../lib/projectStorage';
import type { CalendarPage, CalendarProject } from '../types/calendarProject';
import { Button } from '../components/ui/Button';
import { Logo } from '../components/ui/Logo';

const productLabels = { basic_year_story: 'Basic Year Story', family_planner_cz: 'Family Planner CZ', fineart_gallery: 'FineArt Gallery' };
const weekDays = ['PO', 'ÚT', 'ST', 'ČT', 'PÁ', 'SO', 'NE'];

type OverlayState = {
  bleed: boolean;
  safeZone: boolean;
  grid: boolean;
  pageLabels: boolean;
};

function getPageLabel(page: CalendarPage, index: number) {
  const pageNumber = String(index + 1).padStart(2, '0');
  if (page.type === 'cover') return `Strana ${pageNumber} / Titulka`;
  return `Strana ${pageNumber} / ${page.title.toLocaleLowerCase('cs-CZ')} ${page.year ?? ''}`.trim();
}

function PagePhoto({ project, page, previewUrl }: { project: CalendarProject; page: CalendarPage; previewUrl?: string }) {
  const photo = project.photos.find((item) => item.id === page.photoId);

  if (photo && previewUrl) {
    return <img src={previewUrl} alt={photo.fileName} />;
  }

  return (
    <div className="print-photo-fallback">
      <strong>Lokální fotka není dostupná</strong>
      <span>Nahraj fotky znovu nebo pokračuj v exportu metadat.</span>
    </div>
  );
}

function PrintOverlays({ geometry }: { geometry: PrintGeometry }) {
  return (
    <>
      <div className="print-overlay print-overlay--grid" aria-hidden="true" />
      <div className="print-overlay print-overlay--bleed" style={{ inset: `${geometry.bleedMm}mm` }} aria-hidden="true" />
      <div className="print-overlay print-overlay--safe" style={{ inset: `${geometry.safeZoneMm}mm` }} aria-hidden="true" />
    </>
  );
}

function MonthGrid({ page }: { page: CalendarPage }) {
  const days = getMonthGrid(page.year ?? new Date().getFullYear(), page.monthIndex ?? 1);

  return (
    <div className="print-month-grid">
      {weekDays.map((day) => <strong key={day}>{day}</strong>)}
      {days.map((day, index) => (
        <span key={`${day.date.toISOString()}-${index}`} className={`${day.isCurrentMonth ? '' : 'is-muted'} ${day.isWeekend ? 'is-weekend' : ''} ${day.isHoliday ? 'is-holiday' : ''}`}>{day.day}</span>
      ))}
    </div>
  );
}

function PrintPaper({ project, page, previewUrl, geometry }: { project: CalendarProject; page: CalendarPage; previewUrl?: string; geometry: PrintGeometry }) {
  if (page.type === 'cover') {
    return (
      <article className="print-paper print-paper--cover" style={{ aspectRatio: geometry.cssAspectRatio }}>
        <PrintOverlays geometry={geometry} />
        <div className="print-cover-photo"><PagePhoto project={project} page={page} previewUrl={previewUrl} /></div>
        <div className="print-cover-copy">
          <span>Calendar Studio</span>
          <h1>{page.title}</h1>
          <p>{page.subtitle || String(project.period.year)}</p>
        </div>
      </article>
    );
  }

  return (
    <article className={`print-paper print-paper--month print-layout-${page.layoutId}`} style={{ aspectRatio: geometry.cssAspectRatio }}>
      <PrintOverlays geometry={geometry} />
      <div className="print-photo"><PagePhoto project={project} page={page} previewUrl={previewUrl} /></div>
      <div className="print-calendar-block">
        <div className="print-month-heading">
          <span>{String(page.monthIndex ?? '').padStart(2, '0')}</span>
          <div>
            <h2>{page.title.toLocaleUpperCase('cs-CZ')}</h2>
            {page.notes && <p>{page.notes}</p>}
          </div>
          <strong>{page.year}</strong>
        </div>
        <MonthGrid page={page} />
      </div>
    </article>
  );
}

function PrintPageCard({ project, page, index, previewUrl, geometry }: { project: CalendarProject; page: CalendarPage; index: number; previewUrl?: string; geometry: PrintGeometry }) {
  return (
    <section className="print-page-card">
      <header className="print-page-label">{getPageLabel(page, index)}</header>
      <PrintPaper project={project} page={page} previewUrl={previewUrl} geometry={geometry} />
    </section>
  );
}

export function PrintPreview() {
  const [project, setProject] = useState<CalendarProject | null>(null);
  const [overlays, setOverlays] = useState<OverlayState>({ bleed: false, safeZone: false, grid: false, pageLabels: true });
  const previewUrls = usePhotoPreviewUrls(project);
  const geometry = project ? getPrintGeometry(project.productId) : null;

  useEffect(() => {
    setProject(loadLastProject());
  }, []);

  const toggleOverlay = (key: keyof OverlayState) => {
    setOverlays((current) => ({ ...current, [key]: !current[key] }));
  };

  if (!project || !project.pages.length || !geometry) {
    return (
      <main className="print-preview-shell">
        <section className="print-preview-empty">
          <Logo />
          <h1>Print preview není připravený</h1>
          <p>Nejdřív vytvoř projekt a první návrh kalendáře.</p>
          <Button href="/preflight">Zpět na preflight</Button>
        </section>
      </main>
    );
  }

  const shellClassName = [
    'print-preview-shell',
    `print-preview-shell--${geometry.orientation}`,
    overlays.bleed ? 'show-bleed' : '',
    overlays.safeZone ? 'show-safe-zone' : '',
    overlays.grid ? 'show-grid' : '',
    overlays.pageLabels ? 'show-page-labels' : 'hide-page-labels',
  ].filter(Boolean).join(' ');

  return (
    <main
      className={shellClassName}
      style={{
        '--print-page-width-mm': `${geometry.pageWidthMm}mm`,
        '--print-page-height-mm': `${geometry.pageHeightMm}mm`,
      } as CSSProperties}
    >
      <style media="print">{`@page { size: A3 ${geometry.orientation}; margin: 0; }`}</style>
      <header className="print-preview-header">
        <Logo />
        <div className="print-preview-header__meta">
          <strong>{productLabels[project.productId]}</strong>
          <span>{project.pages.length} stran · {geometry.previewLabel}</span>
          <em>HTML print preview v0.2 není finální PDF/X. V print dialogu nastav A3, měřítko 100 % a vypni headers/footers.</em>
        </div>
        <div className="print-preview-controls" aria-label="DTP overlay vrstvy">
          <label><input type="checkbox" checked={overlays.bleed} onChange={() => toggleOverlay('bleed')} /> Spadávka</label>
          <label><input type="checkbox" checked={overlays.safeZone} onChange={() => toggleOverlay('safeZone')} /> Bezpečná zóna</label>
          <label><input type="checkbox" checked={overlays.grid} onChange={() => toggleOverlay('grid')} /> Mřížka</label>
          <label><input type="checkbox" checked={overlays.pageLabels} onChange={() => toggleOverlay('pageLabels')} /> Popisky stran</label>
        </div>
        <nav>
          <Button href="/preflight" variant="secondary">Zpět na preflight</Button>
          <button type="button" onClick={() => window.print()}>Tisk / uložit jako PDF</button>
        </nav>
      </header>

      <section className="print-preview-stack" aria-label="Print preview stránek">
        {project.pages.map((page, index) => (
          <PrintPageCard key={page.id} project={project} page={page} index={index} previewUrl={previewUrls[page.photoId ?? '']} geometry={geometry} />
        ))}
      </section>
    </main>
  );
}
